import React from 'react';
import { useDispatch } from 'react-redux';
import Stepper from '@mui/material/Stepper';
import Step from '@mui/material/Step';
import StepLabel from '@mui/material/StepLabel';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import TextField from '@mui/material/TextField';
import User from '../domain/user';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormControl from '@mui/material/FormControl';
import FormLabel from '@mui/material/FormLabel';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Chip from '@mui/material/Chip';
import Input from '@mui/material/Input';
import Select from '@mui/material/Select';
import Alert from '@mui/material/Alert';
import {
    verifyEmailAddress,
    verifyPasswordStrength,
    pseudoRandomPasswordGenerate,
    verifyPhonenumber,
    normalizePhonenumber,
} from '../common/common';
import { saveNewUser, checkUsernameAvailability } from '../modules/userdataactions';
import { toast } from 'react-toastify';
import EmailSender from '../modules/emailsender';
import CONFIG from '../config';
import RepresentationsList from './representationslist';
import { useTranslation } from 'react-i18next';

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
    PaperProps: {
        style: {
            maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
            width: 250,
        },
    },
};

const styles = {
    button: {
        mr: 1,
    },
    instructions: {
        mt: 1,
        mb: 1,
    },
    chips: {
        display: 'flex',
        flexWrap: 'wrap',
    },
    chip: {
        m: 0.25,
    },
    stepContent: {
        m: 1,
    },
};

function getSteps() {
    return ['Käyttäjätyyppi', 'Perustiedot', 'Salasana', 'Tallennus', 'Valmis'];
}

export default function NewUserView(props) {
    const dispatch = useDispatch();
    const [activeStep, setActiveStep] = React.useState(0);
    const [newUser, setNewUser] = React.useState(new User());
    const [userType, setUserType] = React.useState(0);
    const [tempPassword, setTempPassword] = React.useState('');
    const [saving, setSaving] = React.useState(false);
    const [checkingUsernameAvailability, setCheckingUsernameAvailability] = React.useState(false);
    const [usernameAvailable, setUsernameAvailable] = React.useState(true);
    const steps = getSteps();
    const rolesForOrgUsers = User.RolesForOrgUsers();
    const rolesForCustomers = User.RolesForCcUsers();
    const { t } = useTranslation();
    let availableRoles = null;

    const handleNext = async () => {
        if (activeStep < 3) {
            if (activeStep === 1) {
                setCheckingUsernameAvailability(true);
                setUsernameAvailable(true);
                const resp = await checkUsernameAvailability(newUser.username, userType)(dispatch);

                setCheckingUsernameAvailability(false);
                if (resp) {
                    setActiveStep((prevActiveStep) => prevActiveStep + 1);
                    setUsernameAvailable(true);
                } else {
                    setUsernameAvailable(false);
                }
            } else {
                setActiveStep((prevActiveStep) => prevActiveStep + 1);
            }
        } else if (activeStep === 3) {
            toast.info(t('toast.savingUserInfo'), { autoClose: 1500, hideProgressBar: true });
            setSaving(true);
            const resp = await saveNewUser(newUser, userType, tempPassword)(dispatch);
            if (resp) {
                toast.info(t('toast.userInfoSaved'), { autoClose: 1500, hideProgressBar: true });
                setActiveStep((prevActiveStep) => prevActiveStep + 1);
                setSaving(false);
            } else {
                toast.error(t('toast.savingUserInfoFailed'), { autoClose: 5000, hideProgressBar: false });
                setSaving(false);
            }
        } else if (activeStep === 4) {
            setSaving(true);
            await EmailSender.send(
                'newuser',
                {
                    USERNAME: newUser.username,
                    PASSWORD: tempPassword,
                    URL: userType === 0 ? CONFIG.frontendUrl : CONFIG.frontendUrlCustomer,
                },
                newUser.username,
                'Uusi käyttäjätunnus'
            );
            props.history.goBack();
        } else {
            throw new Error('');
        }
    };
    const close = () => {
        props.history.goBack();
    };

    const handleBack = () => {
        setActiveStep((prevActiveStep) => prevActiveStep - 1);
    };

    const handleChange = (evt) => {
        const value = evt.target.value;
        const name = evt.target.name;
        let user = new User(newUser);
        switch (name) {
            case 'rolesselect':
                user.roles = [];
                for (let r of value) {
                    user.roles.push(r);
                }
                break;
            case 'username':
                user.username = value;
                break;
            case 'name':
                user.name = value;
                break;
            case 'phonenumber':
                user.phoneNumber = value.trim();
                break;
            case 'password':
                setTempPassword(value);
                return;
            default:
                break;
        }
        setNewUser(user);
    };

    const handlePhoneBlur = () => {
        const normalized = normalizePhonenumber(newUser.phoneNumber);
        if (normalized) {
            const user = new User(newUser);
            user.phoneNumber = normalized;
            setNewUser(user);
        }
    };

    const getStepContent = (step) => {
        switch (step) {
            case 0:
                return userTypeInput();
            case 1:
                return userInfo();
            case 2:
                return temporaryPassword();
            case 3:
                return review();
            case 4:
                return ready();
            default:
                return 'Unknown step';
        }
    };

    const changeUserType = (event) => {
        setUserType(parseInt(event.target.value));
    };

    const ShowUserType = () => {
        const typeStr = userType === 0 ? t('admin.vadoUser') : t('admin.contractCustomer');
        return (
            <Grid item xs={12}>
                {t('admin.userType')}: {typeStr}
            </Grid>
        );
    };

    const ShowUserInfo = () => {
        return (
            <Grid item container>
                <Grid item xs={12}>
                    {t('login.username')}: {newUser.username}
                </Grid>
                <Grid item xs={12}>
                    {t('general.name')}: {newUser.name}
                </Grid>
                <Grid item xs={12}>
                    {t('general.phone')}: {newUser.phoneNumber}
                </Grid>
                <Grid item xs={12}>
                    {t('admin.roles')}:{' '}
                    {newUser.roles.map((r) => (
                        <span key={r}>&nbsp;{r}</span>
                    ))}
                </Grid>
                {userType === 1 && (
                    <Grid container>
                        <Grid item xs={6}>
                            {t('admin.represents')}
                        </Grid>
                        <Grid item xs={6}>
                            <RepresentationsList allowEdit={false} representations={newUser.representations} />
                        </Grid>
                    </Grid>
                )}
            </Grid>
        );
    };

    const ShowTempPassword = () => {
        return (
            <Grid item xs={12}>
                {t('admin.temporaryPassword')}: {tempPassword}
            </Grid>
        );
    };

    const ready = () => {
        return (
            <Grid item container>
                <Grid item xs={12}>
                    {t('admin.userSaved')} <em>{t('admin.sendEmail')}</em>
                </Grid>
                <ShowUserType />
                <ShowUserInfo />
                <ShowTempPassword />
            </Grid>
        );
    };

    const review = () => {
        return (
            <Grid item container>
                <ShowUserType />
                <ShowUserInfo />
                <ShowTempPassword />
            </Grid>
        );
    };

    const generatePassword = () => {
        setTempPassword(pseudoRandomPasswordGenerate(1, 7, 1));
    };

    const temporaryPassword = () => {
        return (
            <Grid item container>
                <ShowUserType />
                <ShowUserInfo />
                <Grid item xs={12}>
                    <TextField
                        required
                        error={!verifyPasswordStrength(tempPassword)}
                        id="password"
                        name="password"
                        label={t('login.password')}
                        value={tempPassword}
                        sx={{ ml: 1, mr: 1, width: '95%' }}
                        margin="normal"
                        onChange={handleChange}
                    />
                </Grid>
                <Grid item xs={12}>
                    <Button
                        variant="contained"
                        color="primary"
                        name="generate-password"
                        onClick={generatePassword}
                        sx={styles.button}>
                        {t('admin.generate')}
                    </Button>
                </Grid>
            </Grid>
        );
    };

    const userTypeInput = () => {
        return (
            <Grid item xs={12}>
                <FormControl component="fieldset">
                    <FormLabel component="legend">{t('admin.userType')}</FormLabel>
                    <RadioGroup aria-label="user-type" name="user-type" value={userType} onChange={changeUserType}>
                        <FormControlLabel value={0} control={<Radio />} label={t('admin.vadoUser')} />
                        <FormControlLabel value={1} control={<Radio />} label={t('admin.contractCustomer')} />
                    </RadioGroup>
                </FormControl>
            </Grid>
        );
    };

    const representationListUpdated = (newRep) => {
        const user = new User(newUser);
        user.representations = newRep;
        setNewUser(user);
    };

    const userInfo = () => {
        return (
            <Grid container item>
                <ShowUserType />

                {userType === 1 && (
                    <Grid item container>
                        <Grid item xs={6}>
                            <strong>{t('admin.represents')}</strong>
                        </Grid>
                        <Grid item xs={6}>
                            <RepresentationsList
                                allowEdit={true}
                                representations={newUser.representations}
                                representationsChanged={representationListUpdated}
                            />
                        </Grid>
                    </Grid>
                )}

                <Grid item xs={12} md={6}>
                    <TextField
                        required
                        error={!verifyEmailAddress(newUser.username)}
                        id="username"
                        name="username"
                        label={t('login.username')}
                        value={newUser.username}
                        sx={{ ml: 1, mr: 1, width: '95%' }}
                        margin="normal"
                        onChange={handleChange}
                    />
                    {!usernameAvailable && <Alert severity="warning">Käyttäjänimi on jo käytössä</Alert>}
                </Grid>
                <Grid item xs={12} md={6}>
                    <TextField
                        required
                        error={false}
                        id="name"
                        name="name"
                        label={t('general.name')}
                        value={newUser.name}
                        sx={{ ml: 1, mr: 1, width: '95%' }}
                        margin="normal"
                        onChange={handleChange}
                    />
                </Grid>
                <Grid item xs={12} md={6}>
                    <TextField
                        required
                        error={newUser.phoneNumber == null || !verifyPhonenumber(newUser.phoneNumber)}
                        id="phonenumber"
                        name="phonenumber"
                        label={t('general.phone')}
                        value={newUser.phoneNumber}
                        sx={{ ml: 1, mr: 1, width: '95%' }}
                        margin="normal"
                        onChange={handleChange}
                        onBlur={handlePhoneBlur}
                    />
                </Grid>
                <Grid item xs={12}>
                    <FormControl sx={{ m: 1, width: '95%' }}>
                        <InputLabel id="rolesLabel">{t('admin.roles')}</InputLabel>
                        <Select
                            labelId="rolesLabel"
                            id="rolesselect"
                            name="rolesselect"
                            multiple
                            value={newUser.roles.map((role) => role)}
                            onChange={handleChange}
                            input={<Input id="selectRoles" />}
                            renderValue={() => (
                                <div sx={styles.chips}>
                                    {newUser.roles.map((role) => (
                                        <Chip key={role} label={role} sx={styles.chip} />
                                    ))}
                                </div>
                            )}
                            MenuProps={MenuProps}>
                            {availableRoles.map((role) => (
                                <MenuItem key={role} value={role}>
                                    {role}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Grid>
            </Grid>
        );
    };

    const okToNextStep = () => {
        switch (activeStep) {
            case 0:
                return true;
            case 1:
                return (
                    verifyEmailAddress(newUser.username) &&
                    newUser.name.length > 3 &&
                    verifyPhonenumber(newUser.phoneNumber) &&
                    newUser.roles.length > 0 &&
                    (userType === 0 || (userType === 1 && newUser.representations.length > 0))
                );
            case 2:
                return verifyPasswordStrength(tempPassword);
            case 3:
                return true;
            case 4:
                return true;
            default:
                throw new Error();
        }
    };

    if (userType === 0) availableRoles = rolesForOrgUsers;
    else availableRoles = rolesForCustomers;

    return (
        <Grid container>
            <Grid item xs={12}>
                <Stepper activeStep={activeStep} alternativeLabel>
                    {steps.map((label, index) => {
                        const stepProps = {};
                        const labelProps = {};
                        return (
                            <Step key={label} {...stepProps}>
                                <StepLabel {...labelProps}>{label}</StepLabel>
                            </Step>
                        );
                    })}
                </Stepper>
            </Grid>
            <Grid item xs={12}>
                <div>
                    <div sx={styles.stepContent}>{getStepContent(activeStep)}</div>
                    <div>
                        <Button
                            disabled={activeStep === 0 || activeStep === 4 || saving || checkingUsernameAvailability}
                            onClick={handleBack}
                            sx={styles.button}>
                            {t('buttons.back')}
                        </Button>
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={handleNext}
                            disabled={!okToNextStep() || checkingUsernameAvailability || saving}
                            sx={styles.button}
                            data-testid="btn-next"
                            name="btn-next">
                            {activeStep === 4 && (
                                <span>
                                    {saving && <i className="fas fa-spinner fa-spin"></i>}&nbsp;{t('admin.sendEmail')}
                                </span>
                            )}
                            {activeStep === 3 && (
                                <span>
                                    {saving && <i className="fas fa-spinner fa-spin"></i>}&nbsp;{t('buttons.save')}
                                </span>
                            )}
                            {activeStep < 3 && (
                                <span>
                                    {checkingUsernameAvailability && <i className="fas fa-spinner fa-spin"></i>}&nbsp;Ok
                                </span>
                            )}
                        </Button>
                        &nbsp;
                        {activeStep === 4 && (
                            <Button
                                variant="contained"
                                color="secondary"
                                onClick={close}
                                disabled={saving}
                                sx={styles.button}
                                name="btn-close">
                                {t('buttons.close')}
                            </Button>
                        )}
                    </div>
                </div>
            </Grid>
        </Grid>
    );
}
